import React, { useEffect, useState } from 'react';
import { UserBadgeGroup } from './UserBadge';
import { HunterChip } from './HunterStats';

const ProfileCard = ({ 
  user, 
  onClose, 
  onViewProfile, 
  onMessage,
  isCurrentUser,
  BACKEND_URL,
  pinPosition // 3D position of the clicked pin
}) => {
  const [cardPosition, setCardPosition] = useState({ x: 100, y: 100 });
  
  // Format date for "Member Since"
  const formatMemberSince = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  
  // Get user type badges
  const getUserTypeBadges = () => {
    const types = user.user_type || [];
    if (types.length === 0 || (typeof types === 'string' && types === 'regular')) {
      return [{ label: 'Music Enthusiast', color: 'bg-gray-500' }];
    }
    
    const badgeConfig = {
      'dj': { label: '🎧 DJ', color: 'bg-cyan-500' },
      'producer': { label: '🎹 Producer', color: 'bg-purple-500' },
      'streamer': { label: '📡 Streamer', color: 'bg-pink-500' },
      'label': { label: '💿 Label', color: 'bg-amber-500' },
      'ar': { label: '🎤 A&R', color: 'bg-fuchsia-500' }
    };
    
    // Handle both array and comma-separated string
    const typeList = Array.isArray(types) ? types : types.split(',').map(t => t.trim());
    return typeList.map(type => badgeConfig[type] || { label: type, color: 'bg-gray-500' }).filter(b => b.label !== 'regular');
  };
  
  // Get availability badges
  const getAvailabilityBadges = () => {
    const badges = [];
    if (user.open_for_bookings) {
      badges.push({ label: '📅 Bookings', color: 'bg-green-600' });
    }
    if (user.open_for_collabs) {
      badges.push({ label: '🤝 Collabs', color: 'bg-blue-600' });
    }
    if (user.accepting_demos) {
      badges.push({ label: '📬 Demos', color: 'bg-amber-600' });
    }
    return badges;
  };
  
  const badges = getUserTypeBadges();
  const availabilityBadges = getAvailabilityBadges();
  
  // Get music genres for "My Vibe"
  const getMusicVibe = () => {
    // Check for favorite_genres from user data
    const genres = user.favorite_genres || [];
    if (genres.length === 0) return null;
    return genres.join(', ');
  };
  
  const musicVibe = getMusicVibe();
  
  // Static card position (no longer linked to pin position)
  useEffect(() => {
    // Fixed position: aligned with yellow line in reference image
    setCardPosition({
      x: 100,
      y: 260  // Moved down to match yellow line exactly
    });
  }, []); // Empty dependency - position never changes
  
  return (
    <div 
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      {/* Profile Card - Compact Size with Animated Border */}
      <div 
        className="absolute w-72 rounded-xl pointer-events-auto"
        style={{
          left: `${cardPosition.x}px`,
          top: `${cardPosition.y}px`,
          padding: '5px', // Space for the border
          background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ffff)',
          backgroundSize: '200% 200%',
          animation: 'flashyBorder 4s linear infinite',
          borderRadius: '16px',
          boxShadow: '0 0 30px rgba(0, 255, 255, 0.6), 0 0 60px rgba(255, 0, 255, 0.4)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes flashyBorder {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
        `}</style>
        
        {/* Inner Card Content */}
        <div 
          className="rounded-xl p-4"
          style={{
            background: 'linear-gradient(135deg, #0f172a, #1e293b, #0f172a)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8) inset'
          }}
        >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header Section - Avatar & Name - More Compact */}
        <div className="flex flex-col items-center mb-3">
          {(() => {
            const displayName = user.user_name || user.name || 'User';
            const initials = displayName
              .split(/\s+/)
              .map(w => w.charAt(0))
              .filter(Boolean)
              .slice(0, 2)
              .join('')
              .toUpperCase() || 'U';
            const avatarUrl = user.profile_picture
              ? (user.profile_picture.startsWith('http')
                  ? user.profile_picture
                  : user.profile_picture.startsWith('/api/')
                    ? `${BACKEND_URL}${user.profile_picture}`
                    : `${BACKEND_URL}/api${user.profile_picture}`)
              : null;
            return (
              <div
                className="relative mb-2"
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid #ffffff',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #06b6d4, #a855f7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Initials fallback layer (always renders behind image) */}
                <span
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '24px',
                    letterSpacing: '0.5px',
                    userSelect: 'none',
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }}
                >
                  {initials}
                </span>
                {avatarUrl && (
                  <img
                    src={avatarUrl}
                    alt=""
                    style={{
                      position: 'relative',
                      width: '64px',
                      height: '64px',
                      minWidth: '64px',
                      minHeight: '64px',
                      maxWidth: '64px',
                      maxHeight: '64px',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '50%',
                      zIndex: 1
                    }}
                    onError={(e) => {
                      // Hide the image entirely so the initials layer shows through
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                {user.online_status && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '2px',
                      right: '2px',
                      width: '12px',
                      height: '12px',
                      background: '#22c55e',
                      borderRadius: '50%',
                      border: '2px solid #0f172a',
                      zIndex: 2
                    }}
                  />
                )}
              </div>
            );
          })()}
          
          <h3 className="text-base font-bold text-white mb-1 inline-flex items-center gap-1.5 justify-center">
            {user.user_name || user.name}
            <UserBadgeGroup
              badges={
                user.role === 'admin'
                  ? ['admin', ...((user.badges) || []).filter(b => b !== 'original_40' && b !== 'admin')]
                  : ((user.badges) || [])
              }
              size="sm"
            />
          </h3>
          
          {/* User Type Badges - Smaller */}
          <div className="flex flex-wrap gap-1 justify-center mb-2">
            {badges.map((badge, index) => (
              <span
                key={index}
                className={`${badge.color} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}
              >
                {badge.label}
              </span>
            ))}
          </div>
          
          {/* Availability Badges */}
          {availabilityBadges.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center mb-2">
              {availabilityBadges.map((badge, index) => (
                <span
                  key={`avail-${index}`}
                  className={`${badge.color} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}
                >
                  {badge.label}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Stats Section - More Compact */}
        <div className="space-y-1 mb-3 text-xs">
          <div className="flex justify-center">
            <HunterChip userId={user.user_id || user.id} />
          </div>
          <div className="flex items-center text-gray-300">
            <span className="mr-1">🎧</span>
            <span>{user.follower_count || 0} followers</span>
          </div>
          
          <div className="flex items-center text-gray-300">
            <span className="mr-1">📅</span>
            <span>Member since {formatMemberSince(user.created_at || user.member_since)}</span>
          </div>
        </div>
        
        {/* Location Section */}
        {(() => {
          const rawCity = user.city || user.home_city;
          const rawCountry = user.country || user.home_country;
          // Skip values that look like raw coordinates (legacy data quality issue)
          const cleanCity = rawCity && !String(rawCity).includes('°') ? String(rawCity) : null;
          const cleanCountry = rawCountry && !String(rawCountry).includes('°') ? String(rawCountry) : null;
          const locationLabel = [cleanCity, cleanCountry].filter(Boolean).join(', ');
          if (!locationLabel) return null;
          return (
            <div className="space-y-1 mb-3 text-xs">
              <div className="flex items-center text-gray-300">
                <span className="mr-1">🏢</span>
                <span className="font-semibold text-cyan-400">
                  {locationLabel}
                </span>
              </div>

              {user.hours_remaining !== undefined && (
                <div className="flex items-center text-gray-300">
                  <span className="mr-1">📍</span>
                  <span>
                    Live Check-in ({Math.floor(user.hours_remaining)}h remaining)
                  </span>
                </div>
              )}
            </div>
          );
        })()}
        
        {/* My Vibe Section - Only show if user has genres */}
        {musicVibe && (
          <div className="mb-3">
            <h4 className="text-xs font-semibold text-purple-400 uppercase mb-1">
              My Vibe
            </h4>
            <p className="text-xs text-gray-300 leading-relaxed">
              {musicVibe}
            </p>
          </div>
        )}
        
        {/* Action Buttons (only if not current user) - More Compact */}
        {!isCurrentUser && (
          <div className="space-y-1.5 pt-3 border-t border-gray-700">
            <button
              onClick={() => onMessage && onMessage(user.user_id || user.id)}
              className="w-full border-2 border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white font-semibold py-1.5 text-sm rounded-lg transition-all duration-200"
            >
              Message
            </button>
          </div>
        )}
        
        {/* Bottom Location Row - Shows context + quick View → CTA for non-self */}
        <div
          className={`${isCurrentUser ? '' : 'pt-3 mt-3 border-t border-gray-700'}`}
          style={{
            marginTop: isCurrentUser ? '12px' : undefined,
            paddingTop: isCurrentUser ? '12px' : undefined,
            borderTop: isCurrentUser ? '1px solid rgba(55, 65, 81, 0.6)' : undefined,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            minHeight: '32px'
          }}
        >
          <span
            style={{
              fontSize: '12px',
              color: '#9ca3af',
              flex: 1,
              textAlign: isCurrentUser ? 'center' : 'left',
              lineHeight: '1.3'
            }}
          >
            {isCurrentUser
              ? 'This is your location'
              : <>This is <span style={{ color: '#22d3ee', fontWeight: 600 }}>{user.user_name || user.name}</span>'s location</>
            }
          </span>

          {!isCurrentUser && (
            <button
              onClick={() => onViewProfile && onViewProfile(user.user_id || user.id)}
              data-testid="profile-card-view-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                borderRadius: '999px',
                background: 'linear-gradient(90deg, rgba(34,211,238,0.18), rgba(168,85,247,0.18))',
                border: '1px solid rgba(34, 211, 238, 0.5)',
                color: '#22d3ee',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                WebkitTapHighlightColor: 'rgba(0,0,0,0)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, rgba(34,211,238,0.32), rgba(168,85,247,0.32))';
                e.currentTarget.style.transform = 'translateX(2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(90deg, rgba(34,211,238,0.18), rgba(168,85,247,0.18))';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              View
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
